/**
 * Menty theme: a small progressive-enhancement layer without dependencies.
 *
 * Everything works as plain forms and links first. This file upgrades them:
 * dialogs (cart drawer, menu, search), add to cart without a page reload,
 * in-place cart updates, the variant picker, product recommendations and the
 * homepage mood switcher.
 */

const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

/* ─── Dialogs ─────────────────────────────────────────────────────────────
 * Any [data-dialog-open="ID"] opens the <dialog id="ID"> as a modal.
 * [data-dialog-close] inside a dialog, Escape, or a click on the backdrop
 * closes it again.
 */
function openDialog(id) {
  const dialog = document.getElementById(id);
  if (!(dialog instanceof HTMLDialogElement)) return false;
  if (!dialog.open) dialog.showModal();
  return true;
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener && openDialog(opener.dataset.dialogOpen)) {
    event.preventDefault();
    return;
  }

  const closer = event.target.closest('[data-dialog-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  // Dialogs have no padding, so a click that lands on the dialog itself hit the backdrop
  if (event.target instanceof HTMLDialogElement && event.target.open) {
    event.target.close();
  }
});

/* ─── Cart ────────────────────────────────────────────────────────────────
 * Every element with [data-cart-section="ID"] shows cart contents. After a
 * cart change, those sections are rendered again in the same request
 * (bundled section rendering) and swapped in.
 */
function cartSectionIds() {
  const ids = new Set(['cart-count']);
  document.querySelectorAll('[data-cart-section]').forEach((element) => ids.add(element.dataset.cartSection));
  return [...ids].slice(0, 5);
}

function renderCartSections(sections) {
  if (!sections) return;

  const activeId = document.activeElement?.id;

  for (const [id, html] of Object.entries(sections)) {
    if (!html) continue;
    const fresh = parseHTML(html).querySelector(`[data-cart-section="${id}"]`);
    if (!fresh) continue;
    document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((element) => {
      element.innerHTML = fresh.innerHTML;
    });
  }

  if (activeId) document.getElementById(activeId)?.focus();
}

function setCartBusy(busy) {
  document.querySelectorAll('[data-cart-section]').forEach((element) => {
    if (busy) element.setAttribute('aria-busy', 'true');
    else element.removeAttribute('aria-busy');
  });
}

function showCartError(message) {
  document.querySelectorAll('[data-cart-error]').forEach((element) => {
    element.textContent = message;
    element.hidden = !message;
  });
}

async function changeCartLine(line, quantity) {
  setCartBusy(true);
  showCartError('');

  try {
    const response = await fetch(`${theme.routes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line,
        quantity,
        sections: cartSectionIds(),
        sections_url: window.location.pathname,
      }),
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || cart.message);

    renderCartSections(cart.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  } catch (error) {
    // Re-render so quantities match the real cart again, then explain what went wrong
    const response = await fetch(`${window.location.pathname}?sections=${cartSectionIds().join(',')}`);
    if (response.ok) renderCartSections(await response.json());
    showCartError(error.message || theme.strings.cartError);
  } finally {
    setCartBusy(false);
  }
}

/* Quantity steppers: the buttons nudge the input, which fires a normal change event */
document.addEventListener('click', (event) => {
  const step = event.target.closest('[data-quantity-step]');
  if (!step) return;

  const input = step.closest('.quantity')?.querySelector('input');
  if (!input) return;

  const increment = Number(input.step) || 1;
  const min = input.min === '' ? 1 : Number(input.min);
  const max = input.max === '' ? Infinity : Number(input.max);
  const next = Math.min(max, Math.max(min, Number(input.value) + Number(step.dataset.quantityStep) * increment));

  if (next === Number(input.value)) return;
  input.value = next;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

let cartChangeTimer;
document.addEventListener('change', (event) => {
  const input = event.target.closest('input[data-cart-line]');
  if (!input) return;

  clearTimeout(cartChangeTimer);
  cartChangeTimer = setTimeout(() => {
    changeCartLine(Number(input.dataset.cartLine), Math.max(0, Number(input.value) || 0));
  }, 300);
});

document.addEventListener('click', (event) => {
  const remove = event.target.closest('[data-cart-remove]');
  if (!remove) return;

  event.preventDefault();
  changeCartLine(Number(remove.dataset.cartRemove), 0);
});

/* ─── Add to cart ─────────────────────────────────────────────────────────
 * Wraps a product form. With the cart drawer enabled, the product is added
 * in the background and the drawer opens; otherwise the form posts normally
 * and Shopify redirects to the cart page.
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
  }

  setError(message) {
    const error = this.querySelector('[data-form-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  async onSubmit(event) {
    if (theme.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;
    event.preventDefault();

    const button = event.submitter || this.form.querySelector('[type="submit"]');
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    this.setError('');

    const formData = new FormData(this.form);
    formData.append('sections', cartSectionIds().join(','));
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message);

      renderCartSections(result.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: result } }));
      openDialog('CartDrawer');
    } catch (error) {
      this.setError(error.message || theme.strings.cartError);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}
customElements.define('product-form', ProductForm);

/* ─── Variant picker ──────────────────────────────────────────────────────
 * Re-renders the product section for the selected option values and swaps
 * the parts that depend on the variant. Supports high-variant products.
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange.bind(this));
  }

  async onChange(event) {
    const target = event.target;
    const productInfo = this.closest('[data-product-info]');
    if (!productInfo) return;

    const selected = [...this.querySelectorAll('input:checked, option:checked')]
      .map((element) => element.dataset.optionValueId)
      .filter(Boolean);

    this.controller?.abort();
    this.controller = new AbortController();

    let html;
    try {
      const url = `${this.dataset.url}?section_id=${this.dataset.sectionId}&option_values=${selected.join(',')}`;
      const response = await fetch(url, { signal: this.controller.signal });
      html = parseHTML(await response.text());
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
      return;
    }

    const freshInfo = html.querySelector('[data-product-info]');
    if (!freshInfo) return;

    // Swap the regions that depend on the selected variant
    productInfo.querySelectorAll('[data-variant-region]').forEach((region) => {
      const fresh = freshInfo.querySelector(`[data-variant-region="${region.dataset.variantRegion}"]`);
      if (fresh) region.innerHTML = fresh.innerHTML;
    });
    document.getElementById(target.id)?.focus();

    // Point the add to cart form at the new variant
    const variantId = freshInfo.dataset.variantId;
    productInfo.dataset.variantId = variantId;
    document.querySelectorAll(`[data-product-form="${this.dataset.sectionId}"] input[name="id"]`).forEach((input) => {
      input.value = variantId;
      input.disabled = !variantId;
    });

    if (variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    }

    // Show the variant's image
    const mediaId = freshInfo.dataset.featuredMediaId;
    if (mediaId) {
      document.dispatchEvent(new CustomEvent('product:media', { detail: { sectionId: this.dataset.sectionId, mediaId } }));
    }
  }
}
customElements.define('variant-picker', VariantPicker);

/* ─── Product gallery ─────────────────────────────────────────────────────
 * A scroll-snap strip on mobile, a stack on desktop. A counter tracks the
 * visible image on mobile, and variant changes bring their image to the front.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.counter = this.querySelector('[data-gallery-current]');

    document.addEventListener('product:media', (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) this.show(event.detail.mediaId);
    });

    if (this.counter && this.viewport) {
      const slides = [...this.viewport.children];
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setCurrent([...this.viewport.children].indexOf(entry.target));
          });
        },
        { root: this.viewport, threshold: 0.6 },
      );
      slides.forEach((slide) => observer.observe(slide));
    }
  }

  setCurrent(index) {
    if (this.counter) this.counter.textContent = index + 1;
  }

  /* Moves the image to the front, so a variant's image is always the first thing you see */
  show(mediaId) {
    const slide = this.viewport?.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;

    if (slide !== this.viewport.firstElementChild) this.viewport.prepend(slide);
    this.viewport.scrollTo({ left: 0, behavior: 'smooth' });
    this.setCurrent(0);
  }
}
customElements.define('product-gallery', ProductGallery);

/* ─── Product recommendations ─────────────────────────────────────────────
 * Loads Shopify's related products once the section scrolls into view.
 */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    observer.observe(this);
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      const fresh = parseHTML(await response.text()).querySelector('product-recommendations');
      if (fresh && fresh.querySelector('.product-card')) {
        this.innerHTML = fresh.innerHTML;
        this.hidden = false;
      }
    } catch (error) {
      console.error(error);
    }
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ─── Mood switcher (homepage hero) ───────────────────────────────────────
 * Accessible tabs that switch between the three foams. The whole hero takes
 * on the selected product's color world, and it rotates on its own until
 * someone interacts with it.
 */
class MoodSwitcher extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.items = [...this.querySelectorAll('[data-mood-item]')];
    this.index = 0;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index, true));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    // In the theme editor, selecting a mood block shows that mood
    this.onBlockSelect = (event) => {
      const index = this.tabs.indexOf(event.target.closest?.('[role="tab"]'));
      if (index !== -1) this.select(index, true);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.dataset.autoplay === 'true' && !reducedMotion && this.tabs.length > 1) {
      this.addEventListener('pointerenter', () => this.pause());
      this.addEventListener('pointerleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.play();
    }
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  play() {
    if (this.stopped) return;
    this.pause();
    this.timer = setInterval(() => this.select((this.index + 1) % this.tabs.length), Number(this.dataset.interval) || 5000);
  }

  pause() {
    clearInterval(this.timer);
  }

  onKeydown(event, index) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let next;
    if (event.key in keys) next = (index + keys[event.key] + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;
    if (next === undefined) return;

    event.preventDefault();
    this.select(next, true);
    this.tabs[next].focus();
  }

  select(index, byUser = false) {
    if (byUser) {
      this.stopped = true;
      this.pause();
    }

    this.index = index;
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
    });
    this.panels.forEach((panel, i) => {
      panel.toggleAttribute('data-active', i === index);
      panel.inert = i !== index;
    });
    this.items.forEach((item) => {
      item.toggleAttribute('data-active', Number(item.dataset.moodItem) === index);
    });

    const world = this.tabs[index]?.dataset.world;
    if (world) this.dataset.world = world;
  }
}
customElements.define('mood-switcher', MoodSwitcher);

/* ─── App showcase ────────────────────────────────────────────────────────
 * Scroll-snap carousel of app screens, like on menty.ai: the centered card is
 * full size, its neighbours shrink and fade with their distance from the
 * center, the glow follows the active screen and the preview video only
 * plays while it's centered and on screen. Mouse users can drag it.
 */
class AppShowcase extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.track.children];
    this.cards = this.slides.map((slide) => slide.querySelector('[data-card]'));
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.video = this.querySelector('[data-video]');
    this.selected = -1;

    this.track.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
    window.addEventListener('resize', () => this.requestUpdate());
    this.prev?.addEventListener('click', () => this.goTo(this.selected - 1));
    this.next?.addEventListener('click', () => this.goTo(this.selected + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dot))));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.goTo(this.selected - 1);
      if (event.key === 'ArrowRight') this.goTo(this.selected + 1);
    });
    this.enableDrag();

    new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        this.syncVideo();
      },
      { rootMargin: '-15% 0px' },
    ).observe(this);

    // Start on the video in the middle, without animating there
    this.goTo(Number(this.dataset.start) || 0, 'instant');
    this.update();
  }

  centerOffset(slide) {
    return slide.offsetLeft + slide.offsetWidth / 2 - this.track.clientWidth / 2;
  }

  goTo(index, behavior = 'smooth') {
    const slide = this.slides[Math.max(0, Math.min(this.slides.length - 1, index))];
    this.track.scrollTo({ left: this.centerOffset(slide), behavior });
  }

  requestUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const center = this.track.scrollLeft + this.track.clientWidth / 2;
    let nearest = 0;
    let nearestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - center);
      // Same curve as menty.ai: the distance as a share of the whole scroll range
      const diff = distance / slide.offsetWidth / Math.max(1, this.slides.length - 1);
      this.cards[index].style.transform = `scale(${Math.max(0.9, 1 - diff * 0.5).toFixed(3)})`;
      this.cards[index].style.opacity = Math.max(0.45, 1 - diff * 1.6).toFixed(3);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = index;
      }
    });

    if (nearest !== this.selected) {
      this.selected = nearest;
      this.style.setProperty('--showcase-glow', this.slides[nearest].dataset.glow);
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === nearest)));
      if (this.prev) this.prev.disabled = nearest === 0;
      if (this.next) this.next.disabled = nearest === this.slides.length - 1;
      this.syncVideo();
    }
  }

  syncVideo() {
    if (!this.video) return;
    const centered = this.slides[this.selected]?.contains(this.video);
    if (centered && this.inView) this.video.play().catch(() => {});
    else this.video.pause();
  }

  enableDrag() {
    let startX = 0;
    let startScroll = 0;
    let dragging = false;

    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      startX = event.clientX;
      startScroll = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      this.track.scrollLeft = startScroll - (event.clientX - startX);
    });

    const end = (event) => {
      if (!dragging) return;
      dragging = false;
      this.track.classList.remove('is-dragging');
      this.track.releasePointerCapture?.(event.pointerId);
      // Let the snap take over from where the drag ended
      this.update();
      this.goTo(this.selected);
    };
    this.track.addEventListener('pointerup', end);
    this.track.addEventListener('pointercancel', end);
  }
}
customElements.define('app-showcase', AppShowcase);

/* ─── Signup forms that turn choices into customer tags ────────────────────
 * Checkboxes with [data-tag] and a select with [data-tag-select] have no name,
 * so they aren't posted themselves; on submit their values are added to the
 * form's contact[tags] field, next to its base tags.
 */
document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[data-tag-builder]');
  const field = form?.querySelector('input[name="contact[tags]"]');
  if (!field) return;

  const chosen = [...form.querySelectorAll('[data-tag]:checked')].map((input) => input.dataset.tag);
  const select = form.querySelector('[data-tag-select]');
  if (select?.value) chosen.push(select.value);

  field.value = [field.dataset.baseTags, ...chosen].filter(Boolean).join(',');
});

/* ─── Forms that submit themselves (sorting, filters, country selector) ── */
document.addEventListener('change', (event) => {
  const form = event.target.closest('form[data-auto-submit]');
  if (form) form.requestSubmit();
});
